import { useState, useEffect } from 'react';
import { Contract, ContractFormData } from './types';
import { getContracts, saveContract, deleteContract } from './utils/storage';
import ContractList from './components/ContractList';
import ContractForm from './components/ContractForm';
import ContractDetail from './components/ContractDetail';
import './App.css';
import './components/ContractStyles.css';

function App() {
  const user = { first_name: 'Alex' };
  const ready = true;
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [view, setView] = useState<'list' | 'form' | 'detail'>('list');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [overdueCount, setOverdueCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready) {
      loadContracts();
    }
  }, [ready]);

  const loadContracts = async () => {
    setLoading(true);
    try {
      const data = await getContracts();
      setContracts(data);
      checkOverdue(data);
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkOverdue = (contractsList: Contract[]) => {
    const now = new Date().toISOString();
    const overdue = contractsList.filter(c => 
      c.status === 'active' && c.deadline < now
    );
    setOverdueCount(overdue.length);
    if (overdue.length > 0) {
      console.log(`⚠️ ${overdue.length} просроченных контрактов`);
    }
  };

  const handleCreateContract = async (formData: ContractFormData) => {
    const newContract: Contract = {
      id: Date.now().toString(),
      ...formData,
      creator: 'alex',
      createdAt: new Date().toISOString(),
      status: 'active'
    };
    await saveContract(newContract);
    await loadContracts();
    setView('list');
  };

  const handleCompleteContract = async (contract: Contract) => {
    const updated = { ...contract, status: 'completed' as const, completedAt: new Date().toISOString() };
    await saveContract(updated);
    await loadContracts();
  };

  const handleDeleteContract = async (id: string) => {
    await deleteContract(id);
    await loadContracts();
    if (view === 'detail') setView('list');
  };

  if (!ready || loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>🤝 Контракты</h1>
        {user && <span className="user-name">{user.first_name}</span>}
        {overdueCount > 0 && (
          <span className="overdue-badge">⚠️ {overdueCount}</span>
        )}
      </header>

      {view === 'list' && (
        <>
          <div className="stats">
            <div className="stat-item">
              <span className="stat-value">{contracts.filter(c => c.status === 'active').length}</span>
              <span className="stat-label">Активные</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{contracts.filter(c => c.status === 'completed').length}</span>
              <span className="stat-label">Выполнено</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{contracts.length}</span>
              <span className="stat-label">Всего</span>
            </div>
          </div>

          <button 
            className="btn btn-primary create-btn"
            onClick={() => setView('form')}
          >
            ➕ Новый контракт
          </button>

          <ContractList 
            contracts={contracts}
            onSelect={(c) => { setSelectedContract(c); setView('detail'); }}
            onDelete={handleDeleteContract}
          />
        </>
      )}

      {view === 'form' && (
        <ContractForm 
          onSubmit={handleCreateContract}
          onCancel={() => setView('list')}
        />
      )}

      {view === 'detail' && selectedContract && (
        <ContractDetail 
          contract={selectedContract}
          onBack={() => setView('list')}
          onComplete={handleCompleteContract}
          onDelete={handleDeleteContract}
        />
      )}
    </div>
  );
}

export default App;
